'use client'

import { Input } from '@/app/components/common/Input'

export interface AddressData {
  address1: string
  address2?: string
  city: string
  state: string
  zipCode: string
}

interface AddressInputProps {
  address: Partial<AddressData>
  onChange: (address: Partial<AddressData>) => void
  errors?: Partial<Record<keyof AddressData, string>>
  label?: string
  required?: boolean
  disableBrowserAutofill?: boolean
}

export function AddressInput({
  address,
  onChange,
  errors = {},
  label = 'Address',
  required = false,
  disableBrowserAutofill = false,
}: AddressInputProps) {
  const updateField = (field: keyof AddressData, value: string) => {
    onChange({ ...address, [field]: value })
  }

  return (
    <div data-component-name="AddressInput" className="space-y-4">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-4">
          {label} {required && <span className="text-danger">*</span>}
        </h4>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Street Address {required && <span className="text-danger">*</span>}
            </label>
            <Input
              type="text"
              value={address.address1 || ''}
              onChange={(e) => updateField('address1', e.target.value)}
              placeholder="123 Main St"
              fullWidth
              error={errors.address1}
              autoComplete={disableBrowserAutofill ? 'off' : undefined}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Apartment, Suite, etc. (Optional)
            </label>
            <Input
              type="text"
              value={address.address2 || ''}
              onChange={(e) => updateField('address2', e.target.value)}
              placeholder="Apt 4B"
              fullWidth
              error={errors.address2}
              autoComplete={disableBrowserAutofill ? 'off' : undefined}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City {required && <span className="text-danger">*</span>}
              </label>
              <Input
                type="text"
                value={address.city || ''}
                onChange={(e) => updateField('city', e.target.value)}
                placeholder="New York"
                fullWidth
                error={errors.city}
                autoComplete={disableBrowserAutofill ? 'off' : undefined}
              />
            </div>

            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State {required && <span className="text-danger">*</span>}
              </label>
              <Input
                type="text"
                value={address.state || ''}
                onChange={(e) => updateField('state', e.target.value)}
                placeholder="NY"
                maxLength={2}
                fullWidth
                error={errors.state}
                autoComplete={disableBrowserAutofill ? 'off' : undefined}
              />
            </div>

            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP Code {required && <span className="text-danger">*</span>}
              </label>
              <Input
                type="text"
                value={address.zipCode || ''}
                onChange={(e) => updateField('zipCode', e.target.value)}
                placeholder="10001"
                maxLength={10}
                fullWidth
                error={errors.zipCode}
                autoComplete={disableBrowserAutofill ? 'off' : undefined}
              />
            </div>
          </div>
        </div>

        {address.address1 && address.city && address.state && address.zipCode && (
          <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600">
            <span className="font-medium">Saved address: </span>
            {address.address1}
            {address.address2 && `, ${address.address2}`}, {address.city}, {address.state}{' '}
            {address.zipCode}
          </div>
        )}
      </div>
    </div>
  )
}
